<template>
  <modal
    height="750px"
    width="1240px"
    bodyPadding="0"
    bodyClass="body__columns-settings"
    :title="t('Columns settings')"
    @close="checkClose"
    :read-article-url="`https://docs.bemob.com/${locale}/columns-settings`"
  >
      <div class="header-row" v-if="!activeTab">
        <div class="subtitle pl-24">
          <span>{{ t('Presets') }}</span>
          <ui-tooltip-icon
            preset="help"
            size="14"
            class="ml-4"
          >
          <span v-html="t('A preset is a set of columns that you customize yourself.<br>Create your own column presets and then quickly select them in reports.')"></span>
          </ui-tooltip-icon>
          <div class="header-row-buttons" ref="buttons">
            <ui-button
              size="mini"
              v-for="({presetId, presetName},index) in presetsInfo"
              @click="selectPreset({presetId, presetName})"
              class="header-row-button"
              :forceHideChevron="true"
              :icon="presetId === GENERAL_PRESET && 'fas fa-home' "
              :iconAppend="(presetId === activePreset) && presetId !== GENERAL_PRESET && 'fas fa-pen' "
              :button-style="activePreset === presetId ? 'accent' : 'plain'"
              :text="presetId === GENERAL_PRESET ? t('____x.generalPreset') : presetName"
              :dontOpenMenu="activePreset !== presetId || presetId === GENERAL_PRESET"
              ref="presetsRefs"
              :key="presetId"
              :menuProps="{
                left: true,
                right: false,
                bottom: true, 
                nudgeTop: -2,
                nudgeRight: 0,
              }"
            >
              <template #menu>
                <div class="preset-menu">
                  <ui-input
                    v-model="inputPresetName"
                    :placeholder="t('Preset Name')"
                    :label="t('Preset Name')"
                    :error-messages="errorsDict['name']"
                    class="mb-4"
                  />
                  <div class="preset-buttons">
                    <div>
                      <ui-button
                        icon="fas fa-trash-alt"
                        @click="openDeletePresetDialog({presetId,index})"
                        :disabled="presetSaveLoading"
                        :loading="presetDeleteLoading"
                        buttonStyle="outline-alt"
                        :text="t('Delete')"
                      ></ui-button>
                    </div>
                    <div>
                      <ui-button
                        :text="t('Cancel')"
                        icon="fas fa-times"
                        @click="closeMenuPreset({index})"
                        :disabled="presetDeleteLoading || presetSaveLoading"
                      ></ui-button>

                      <ui-button
                        :text="t('Save')"
                        icon="fas fa-check"
                        button-style="accent"
                        @click="savePreset({presetId,index})"
                        :disabled="presetDeleteLoading"
                        :loading="presetSaveLoading"
                      ></ui-button>
                    </div>
                  </div>
                </div>
              </template>
            </ui-button>
            <ui-button
              size="mini"
              v-if="presetsInfo.length < store.billing.maxQuantity.presetAmount"
              class="add-preset-button"
              icon="fas fa-plus"
              ref="newPreset"
              @click="clickNewPreset"
              plan-limits="presetAmount"
              :dontOpenMenu="true"
              :menuProps="{
                left: true,
                right: false,
                bottom: true, 
                nudgeTop: -2,
                nudgeRight: 0,
              }"
            >
              <template #menu>
                <div class="preset-menu">
                  <ui-input
                    v-model="inputPresetNameNew"
                    :placeholder="t('Preset Name')"
                    :label="t('Preset Name')"
                    :error-messages="errorsDict['name']"
                    class="mb-4"
                  />
                  <div class="preset-buttons preset-buttons__new">
                    <ui-button
                      :text="t('Cancel')"
                      icon="fas fa-times"
                      @click="closeMenuPreset({ref:'newPreset'})"
                      :disabled="presetSaveLoading"
                    ></ui-button>
  
                    <ui-button
                      :text="t('Save')"
                      icon="fas fa-check"
                      button-style="accent"
                      @click="savePreset({ref:'newPreset'})"
                      :loading="presetSaveLoading"
                    ></ui-button>
                  </div>
                </div>
              </template>
            </ui-button>
          </div>
        </div>
      </div>
      <div class="dialog-wrapper dialog-wrapper__columns-settings">
        <div class="details metrics-picker-dialog__panel metrics-picker-dialog__panel--columns">
          <div class="subtitle justify-space-between pl-24">
            <span>1. {{ t('Columns visibility') }}</span>
            <div class="metrics-picker-dialog__columns-counter">
              {{ selectedLength }} / {{ existing_metrics.length }} {{ tc('plural-columns-specific-form', existing_metrics.length ) }}
            </div>
          </div>

          <div class="metrics-picker-dialog__padding-fix--mod-search-abs">
            <div class="mod-search-abs">
              <ui-icon size="15" :color="$colors.gray40" icon="fas fa-search"></ui-icon>
              <v-text-field
                v-model="search"
                class="controls-hide-details"
                variant="plain"
                hide-details
                :placeholder="t('Search...')"
              >
              </v-text-field>
            </div>
          </div>

            <div ref="listOfGroups" class="metrics-picker-dialog__panel-one metrics-picker-dialog__scroll-container metrics-picker-dialog__padding-fix--full">
              <ui-checkbox
                v-if="filteredMetrics.length"
                class="select-all"
                v-model="selectAll"
                :label="t('All Columns')"
                @change="handleSelectAllChanged"
              ></ui-checkbox>
                <columns-settings-dialog-column
                    v-if="pagesWithoutGrouping || search.length"
                    v-for="(item) in filteredMetrics"
                    :item="item"
                    :onAddNewColumn="onAddNewColumn"
                    :onDelete="onDelete"
                    :getHelp="getHelp"
                    :selectedMetrics="selectedMetrics"
                    :selectedMetricsChanged="selectedMetricsChanged"
                    :reportMetricsName="reportMetricsName"
                />
                <draggable
                  v-if="filteredMetrics.length && !pagesWithoutGrouping && !search.length"
                  group="columns-group"
                  v-model="filteredMetricsGrouped"
                  ghost-class="draggable-ghost"
                  class="groupOfRows"
                  :item-key="(groupData) => groupData.group.id"
                >
                  <template #item="{ element: groupData, index: groupIndex }">
                  <div>
                    <div class="group-name">
                      <div class="group-name__collapse" @click="setIsOpenedGroup(groupData.group.id)" />
                      <ui-checkbox
                        v-if="groupData.columns.length"
                        class="group-name--label"
                        :label="groupingTranslation(groupData.group)"
                        :modelValue="isGroupSelected(groupData)"
                        @change="handleSelectAllInGroup(groupIndex)"
                        :help-tooltip="groupingTooltipTranslation(groupData.group)"
                      ></ui-checkbox>
                      <ui-label 
                        class="ml-20 group-name--label"
                        :help-tooltip="groupingTooltipTranslation(groupData.group)"
                        :textBold="false" 
                        v-else
                        :text="groupingTranslation(groupData.group)"
                      />
                      <div class="group-setting">
                        {{ groupData.group.selected }}/{{ groupData.columns.length}} {{ tc('plural-columns-specific-form', groupData.columns.length ) }}
                        <div class="is-opened-separator"></div>
                        <span class="is-opened-text">{{groupData.group.isOpened ? t('Hide') : t('Show')}}</span>
                        <ui-icon
                          :color="$colors.green"
                          size="8"
                          :icon="`fas fa-chevron-${groupData.group.isOpened ? 'up' : 'down'}`"
                        ></ui-icon>
                      </div>
                    </div>

                    <div class="drag-the-column-here" v-if="groupData.group.isOpened && !groupData.disabledColumns.length && !groupData.columns.length">
                      {{t('Drag the column here')}}
                    </div>
                    <template
                      v-for="item in groupData.disabledColumns"
                      :key="item.value"
                    >
                      <div
                        v-show="groupData.group.isOpened"
                        v-if="item && isDisplayColumn(item) && groupData.disabledColumns.length"
                        class="metrics-picker-dialog__metric-list-item"
                      >
                        <columns-settings-dialog-column
                          :item="item"
                          :groupIndex="groupIndex"
                          :onAddNewColumn="onAddNewColumn"
                          :onDelete="onDelete"
                          :getHelp="getHelp"
                          :selectedMetrics="selectedMetrics"
                          :selectedMetricsChanged="selectedMetricsChanged"
                          :reportMetricsName="reportMetricsName"
                        />
                      </div>
                    </template>
                    <draggable
                      :group="{ name: 'row' }"
                      :list="groupData.columns"
                      @end="columnHandleDragEnd"
                      ghost-class="draggable-ghost-row"
                      item-key="value"
                    >
                      <template #item="{ element: item }">
                        <div
                          v-show="groupData.group.isOpened && item && !item.disabled && isDisplayColumn(item)"
                          class="metrics-picker-dialog__metric-list-item"
                        >
                          <columns-settings-dialog-column
                            v-if="item"
                            :item="item"
                            :groupIndex="groupIndex"
                            :onAddNewColumn="onAddNewColumn"
                            :onDelete="onDelete"
                            :getHelp="getHelp"
                            :selectedMetrics="selectedMetrics"
                            :selectedMetricsChanged="selectedMetricsChanged"
                            :reportMetricsName="reportMetricsName"
                          />
                        </div>
                      </template>
                    </draggable>
                  </div>
                  </template>
                </draggable>
              <ui-not-found class="ui-not-found" v-if="!filteredMetrics.length"></ui-not-found>
            </div>
        </div>

          <div class="details metrics-picker-dialog__panel metrics-picker-dialog__panel--order">
            <div class="subtitle pl-24">
              <span>2. {{ t('Columns order') }}</span>
              <ui-tooltip-icon
                preset="help"
                class="ml-8"
                :text="t('Columns will be displayed according to the order shown below. Drag columns to change order')"
              ></ui-tooltip-icon>
            </div>

            <div class="metrics-picker-dialog__metrics-order-list metrics-picker-dialog__scroll-container metrics-picker-dialog__padding-fix--full">
              <template
                v-for="item in orderedMetricsTree"
                :key="item.value"
              >
                <div
                  v-if="item && item.disabled && isDisplayColumn(item)"
                  class="metrics-picker-dialog__metrics-order metrics-picker-dialog__metrics-order--disabled"
                >
                  <span class="metrics-picker-dialog__metrics-order-text">{{reportMetricsName(item.value)}}</span>
                  <div class="metrics-picker-dialog__metrics-order-utility align-center">
                    <ui-icon
                      v-if="item.disabled || groupingValuesValueList.includes(item.value)"
                      class="metrics-picker-dialog__metrics-order-icon"
                      size="13"
                      :color="$colors.gray40"
                      :icon="!item.disabled && groupingValuesValueList.includes(item.value) ? 'fas fa-file-download' : 'fas fa-lock'"
                    >
                    </ui-icon>
                    <div class="metrics-picker-dialog__hint-wrapper">
                      <span v-if="item.help" class="metrics-picker-dialog__hint" v-html="getHelp(item)"></span>
                      <span v-esle>&nbsp;</span>
                    </div>
                  </div>
                </div>
              </template>

              <draggable
                v-model="orderedMetricsMovable"
                group="metric"
                item-key="value"
                @start="handleDragStart"
                @end="handleDragEnd"
              >
                <template #item="{ element: item }">
                <div
                  class="metrics-picker-dialog__metrics-order metrics-picker-dialog__metrics-order--movable"
                  :class="{ 'metrics-picker-dialog__metrics-order--dragged': item.value === dragged }"
                  :data-value="item.value"
                >
                  <span class="metrics-picker-dialog__metrics-order-text">{{reportMetricsName(item.value)}}</span>
                  <div class="metrics-picker-dialog__metrics-order-utility align-center">
                    <ui-icon
                      class="metrics-picker-dialog__metrics-order-icon"
                      size="13"
                      :color="$colors.gray40"
                      icon="fas fa-grip-lines"
                    ></ui-icon>
                    <div class="metrics-picker-dialog__hint-wrapper">
                      <span v-if="item.help" class="metrics-picker-dialog__hint" v-html="getHelp(item)" />
                      <span v-else>&nbsp;</span>
                    </div>
                  </div>
                </div>
                </template>
              </draggable>
            </div>
          </div>
      </div>

    <template #footer>
      <section class="justify-space-between xs12" style="display: flex; width: 100%">
        <div style="display: flex;" class="align-center">
          <ui-button
            v-if="canEditCustomColumns && hasNewColumnBtn"
            class="hidden-sm-and-down"
            :icon="actions.newColumn.icon"
            :text="actions.newColumn.title"
            @click="actions.newColumn.click"
            planLimits='customColumnsAmount'
          ></ui-button>
          <ui-button
            class="hidden-sm-and-down"
            :icon="actions.resetColumns.icon"
            :text="actions.resetColumns.title"
            @click="actions.resetColumns.click"
          ></ui-button>
          <ui-button-multi
            class="hidden-sm-and-down"
            :menu-list="resetResizeMenuList"
            :ignore-first="true"
            :ignore-rest="resetResizeMenuList.length === 1"
            :menu-props="{
              'top': true,
              'left': true,
              'offsetY': true,
              'nudgeBottom': 0,
              'nudgeTop': 20
            }"
          ></ui-button-multi>

          <ui-button
            planLimits='customColumnsAmount'
            class="hidden-md-and-up"
            v-if="canEditCustomColumns && hasNewColumnBtn"
            :icon="actions.newColumn.icon"
            @click="actions.newColumn.click"
          ></ui-button>

          <ui-button
            class="hidden-md-and-up"
            :icon="actions.resetResize.icon"
            :has-menu="true"
            :menu-list="resetMenuList"
            :menu-content-class="'hidden-md-and-up'"
            :menu-props="{
              'top': true,
              'right': true,
              'left': false,
              'offsetY': true,
              'offsetX': true,
              'nudgeBottom': 0,
              'nudgeTop': 20,
              'nudgeRight': -35,
            }"
          ></ui-button>
        </div>

        <div class="d-flex justify-end align-center">
          <ui-button
            icon="fas fa-times"
            :text="t('Cancel')"
            @click="checkClose()"
          ></ui-button>

          <ui-button-multi
            :menu-list="applyMenuList"
            :ignore-first="true"
            :ignore-rest="applyMenuList.length === 1"
            button-style="accent"
            :menu-props="{
              'top': true,
              'left': true,
              'offsetY': true,
              'nudgeBottom': 0,
              'nudgeTop': 20
            }"
          ></ui-button-multi>
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import Draggable from 'vuedraggable';
import ColumnsSettingsDialogColumn from '@/views/reports/pickers/cores/columns-settings-dialog/columns-settings-dialog-column.vue';
import { useColors, useI18n } from '@/composables';
import { useStore } from '@/store';
import { useColumnsSettingsDialogState } from '@/composables/useColumnsSettingsDialogState';

const emit = defineEmits<{
  close: [];
}>();

const $colors = useColors();
const store = useStore();
const { locale } = useI18n();

const {
  reportParamsApi,
  presetsInfo,
  t,
  tc,
  GENERAL_PRESET,
  errorsDict,
  activeTab,
  activePreset,
  search,
  selectedMetrics,
  filteredMetricsGrouped,
  orderedMetricsTree,
  orderedMetricsMovable,
  dragged,
  selectAll,
  inputPresetName,
  inputPresetNameNew,
  presetSaveLoading,
  presetDeleteLoading,
  canEditCustomColumns,
  pagesWithoutGrouping,
  existing_metrics,
  filteredMetrics,
  selectedLength,
  groupingValuesValueList,
  reportMetricsName,
  hasNewColumnBtn,
  actions,
  applyMenuList,
  resetMenuList,
  resetResizeMenuList,
  presetsRefs,
  newPreset,
  listOfGroups,
  buttons,
  checkClose,
  selectPreset,
  clickNewPreset,
  savePreset,
  closeMenuPreset,
  openDeletePresetDialog,
  handleSelectAllChanged,
  handleSelectAllInGroup,
  isGroupSelected,
  groupingTranslation,
  groupingTooltipTranslation,
  setIsOpenedGroup,
  columnHandleDragEnd,
  onAddNewColumn,
  onDelete,
  getHelp,
  selectedMetricsChanged,
  isDisplayColumn,
  handleDragStart,
  handleDragEnd,
} = useColumnsSettingsDialogState(emit);
</script>


<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';

.select-all {
  padding-bottom: 12px;
  width: 100%;
}

.ui-not-found {
  margin-top: 15px;
}

.header-row{
  display:flex;
  .subtitle{
    width:100%;
    @media only screen and (max-width: 700px) {
      display:block;      
    }
  }
}

.header-row-buttons{
  display:flex;
  margin-left:8px;
  max-width: calc(100% - 100px);
  flex-grow: 1;
  @media only screen and (max-width: 700px) {
    margin-left:0;
    margin-top:4px;
    max-width: 100%;
  }
}

.header-row-button{
  margin-right:8px;
  max-width: calc(20% - 8px);
  margin-left:0!important;
  :deep(button){
    max-width: 100%;
    .content{
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.add-preset-button{
  margin-left:0!important;
  :deep(button){
    padding:0;
    min-width:24px;
    justify-content: center;
    display: flex;

    .ui-button__main {
      justify-content: center;
      width: 100%;
    }
  }
}

.preset-menu{
  width:320px;
  padding:8px;
  background: #fff;
  border-radius: 8px!important;
}

.preset-buttons{
  border-top: 1px solid var(--color-gray10);
  display:flex;
  padding: 8px 0 0 0;
  justify-content: space-between;
  :deep(button){
    margin-right:0!important;
  }
  div{
    display:flex;
    justify-content: flex-end;
  }
}

.preset-buttons__new{
  justify-content: flex-end;
}

.dialog-wrapper__columns-settings{
  height:auto;

  .metrics-picker-dialog__panel {
    flex: 1 1 100%;
    min-width: 0;

    @media (min-width: 960px) {
      flex-grow: 0;
    }
  }

  .metrics-picker-dialog__panel--columns {
    @media (min-width: 960px) {
      flex: 0 0 58.333333%;
      max-width: 58.333333%;
    }
  }

  .metrics-picker-dialog__panel--order {
    @media (min-width: 960px) {
      flex: 0 0 41.666667%;
      max-width: 41.666667%;
    }
  }
}
</style>

<style lang="scss">
.metrics-picker-dialog {
  .v-card {
    height: 100%;
    max-height: 720px;
  }

  &__hint-wrapper {
    display: flex;
    align-items: center;
    min-height: 24px;
    margin-top: 4px;
    padding-bottom: 4px;
    word-break: break-word;
  }

  &__hint {
    text-transform: none;
    line-height: 15px;
    font-size: 12px;
    color: var(--color-gray40);
  }

  &__panel-one {
    padding: 10px 10px 10px 13px;
  }

  &__metrics-order-list {
    padding: 7px 10px;
  }

  &__metrics-order-list > &__metrics-order:first-child {
    border-top: none;
  }

  &__metrics-order {
    display: flex;
    justify-content: space-between;
    width: 100%;
    min-height: 32px;
    cursor: grab;
    border-top: 1px solid var(--color-gray10);
    align-items: center;

    &--movable {
      &:hover {
        background-color: var(--color-yellowlight);
      }
    }

    &--dragged {
      background-color: var(--color-yellowlight);
    }

    &--disabled {
      cursor: default;
      color: var(--color-graphite);
      position: relative;
    }

    &:hover {
      .metrics-picker-dialog__metrics-order-icon {
        opacity: 1;
      }
    }
  }

  &__metrics-order-icon {
    opacity: 0;
    line-height: 16px;
    margin-right: 4px;
  }

  &__metrics-order-text {
    font-size: 13px;
    line-height: 16px;
    width: 50%;
    word-break: break-word;
    padding: 0 4px 4px 0;
  }

  &__panel-one-header {
    display: flex;
    justify-content: space-between;
    height: 48px;
    min-height: 48px;
  }

  &__columns-counter {
    color: var(--color-grass);
    font-size: 13px;
  }

  &__panel-two-header {
    display: flex;
    height: 48px;
    min-height: 48px;
  }

  &__panel-two-title {
    padding-right: 8px
  }

  &__column-checkbox-wrapper {
    display: flex;
    justify-content: flex-start;
    min-height: 32px;

    .v-label {
      padding-right: 10px;
      word-break: break-word;
    }
  }

  &__column-checkbox {
    flex-grow: 0;
    margin-left: -3px;
  }

  &__column-checkbox-tooltip {
    align-self: center;
    margin-left: 10px;
  }

  &__content {
    display: flex;
    width: 100%;
  }

  &__apply-button-icon {
    margin-left: 8px;
    margin-top: -2px;
  }

  &__panel {
    display: flex;
    flex-direction: column;
  }

  &__scroll-container {
    flex-shrink: 1;
    flex-grow: 0;
    min-height: 0;
    overflow-y: auto;
  }

  &__metric-list-item {
    display: block;
    cursor:grab;
    padding-left: 5px;
    margin-left: -5px;

    & + & {
      border-top: 1px solid var(--color-gray10);
    }
  }

  &__metrics-order-utility {
    display: flex;
    justify-content: space-between;
    flex-direction: row-reverse;
    width: 50%;
    position: relative;
  }

  &__padding-fix {
    padding-left: 14px;

    &--full {
      padding-left: 24px;
    }
  }
}

.metrics-picker-dialog__padding-fix--mod-search-abs {
  padding-left: 9px;
  border-bottom: 1px solid var(--color-gray10);

  .mod-search-abs {
    position: relative;
    display: flex;
    min-height: 43px;

    .ui-icon {
      z-index: 3;
      position: absolute;
      top: calc(50% - 7px);
      left: 16px;
    }

    .v-input {
      padding: 0 !important;
    }

    .v-input__details {
      display: none;
    }

    .v-field {
      border-radius: 0;
      background: transparent;
      box-shadow: none;
    }

    .v-field__overlay,
    .v-field__outline {
      display: none;
    }

    .v-input__control,
    .v-field,
    .v-field__field,
    .v-field__input,
    input {
      height: 43px;
      min-height: 43px;
    }

    input {
      margin: 0 !important;
      border: none !important;
      border-radius: 0 !important;
      padding: 0 26px 0 45px !important;
      background: var(--color-white);
      line-height: 22px;

      &::placeholder {
        opacity: 0.4;
        font-size: 15px;
      }
    }
  }
}

.group-name{
  display:flex;
  align-items: center;
  background: var(--color-gray5);
  border-radius: 8px;
  padding: 8px 12px;
  justify-content: space-between;
  margin: 0 0 8px -12px;
  cursor:pointer;
  position:relative;
}

.group-name__collapse{
  position: absolute;
  top:0;
  left:0;
  right:0;
  bottom:0;
}

.group-name--label{
  padding-bottom:0!important;
  position:relative;
  .ui-label{
    padding-right:1px; // fix cut help icon
  }
}

.group-setting{
  display: flex;
  align-items: center;
}

.is-opened-separator{
  width: 1px;
  height: 16px;
  background: var(--color-gray10);
  margin:0 8px;
}

.is-opened-text{
  color: var(--color-green);
  font-size:12px;
  text-transform: capitalize;
  margin-right:4px;
}

.drag-the-column-here{
  text-align:center;
  padding:0 0 8px 0;
}

.draggable-ghost-row{
  background: var(--color-yellowlight);
}

.groupOfRows{
  .draggable-ghost{
    padding-bottom:8px;
    .group-name{
      background: var(--color-yellowlight);
    }
    .metrics-picker-dialog__metric-list-item{
      background: var(--color-yellowlight);
    }
  }
}

.body__columns-settings{
  display: flex;
  flex-direction: column;
}

</style>
